import { ReactElement, useCallback } from "react";
import { Control, Controller, Path, FieldValues } from "react-hook-form";
import {
  FormControl,
  Select,
  InputLabel,
  FormHelperText,
  MenuItem,
  SelectProps,
} from "@mui/material";

interface IFormSelectFieldOption<TValue> {
  label: string;
  value: TValue;
}

export type FormSelectFieldOptions<TValue = string> =
  IFormSelectFieldOption<TValue>[];

export type FormSelectFieldProps<
  TFormValues extends FieldValues,
  TValue extends string | number = string,
> = Omit<SelectProps<TValue | TValue[]>, "value" | "onChange"> & {
  name: Path<TFormValues>;
  control: Control<TFormValues>;
  options: FormSelectFieldOptions<TValue>;
  helperText?: string;
};

export const FormSelectField = <
  TFormValues extends FieldValues,
  TValue extends string | number = string,
>({
  className,
  name,
  control,
  options,
  helperText,
  label,
  multiple,
  placeholder = "Select...",
  size,
  ...restProps
}: FormSelectFieldProps<TFormValues, TValue>): ReactElement => {
  const renderMultipleValue = useCallback((selected: TValue | TValue[]) => {
    if (Array.isArray(selected)) {
      return selected.join(", ");
    }

    return selected;
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { value, name, ...restField } = field;

        return (
          <FormControl className={className} error={!!error} size={size}>
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              error={!!error}
              value={value || (multiple ? [] : "")}
              multiple={multiple}
              renderValue={multiple ? renderMultipleValue : undefined}
              {...restField}
              {...restProps}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{error?.message || helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};
